import { RefundRequestItem } from '@/lib/api/financial/billing-refund-api'
import { isOfflinePaymentMethod } from '@/lib/api/common/interfaces/billing.interface'
import { useNavigate } from 'react-router-dom'
import {
    MdAccountBalance,
    MdCheck,
    MdClose,
    MdDelete,
    MdEvent,
    MdReplay,
} from 'react-icons/md'
import ApprovalCard from './approval-card'
import CompanyBadge from './company-badge'
import { formatCurrency, formatDate } from '../format'

interface RefundCardProps {
    item: RefundRequestItem
    selectable: boolean
    selected: boolean
    onToggleSelect: () => void
    canApprove: boolean
    onApprove: () => void
    onReject: () => void
    onMarkExternal: () => void
    onClearExternal: () => void
    marking: boolean
    /**
     * Tira o pedido de estorno da fila sem decidir — para o que foi aberto por engano.
     *
     * Só vem preenchido para quem tem `delete` em `financial:billing_approvals`.
     * Diferente de "Recusar", que é decisão e fica no histórico: aqui a solicitação
     * deixa de existir e a cobrança volta ao estado normal, aceitando um pedido novo.
     */
    onDelete?: () => void
}

/** Solicitação de estorno — devolve ao cliente dinheiro já recebido. */
export default function RefundCard({
    item,
    selectable,
    selected,
    onToggleSelect,
    canApprove,
    onApprove,
    onReject,
    onMarkExternal,
    onClearExternal,
    marking,
    onDelete,
}: RefundCardProps) {
    const navigate = useNavigate()
    /** Dinheiro, maquininha, "outro": o valor não passou pela conta digital. */
    const isOffline = isOfflinePaymentMethod(item.billing.paymentMethod)

    const amount = item.payload?.refundAmount ?? item.billing.refundableAmount
    const paid = item.billing.paidAmount ?? item.billing.amount
    // Devolver menos do que entrou é estorno parcial: o operador precisa ver isso
    // antes de aprovar (parcial não revoga repasse nem retira pontos do cliente).
    const isPartial = amount < paid

    const client =
        item.billing.client?.name ||
        item.billing.partner?.tradingName ||
        item.billing.partner?.name ||
        null

    return (
        <ApprovalCard
            selectable={selectable}
            selected={selected}
            onToggleSelect={onToggleSelect}
            eyebrow={
                <>
                    <span className="badge badge-sm badge-warning gap-1">
                        <MdReplay className="text-sm" />
                        Estorno
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <MdEvent className="text-sm" />
                        {formatDate(item.createdAt)}
                    </span>
                    <span>·</span>
                    <span>Por {item.requestedBy?.name || 'sistema'}</span>
                    <CompanyBadge company={item.company} />
                </>
            }
            title={
                <button
                    className="link link-hover text-left hover:text-secondary"
                    onClick={() =>
                        navigate(`/financial/billings/${item.billingId}`)
                    }
                >
                    {item.billing.description || `Cobrança #${item.billingId}`}
                </button>
            }
            subtitle={client}
            amount={<span className="text-error">{formatCurrency(amount)}</span>}
            amountHint={
                <>
                    de {formatCurrency(paid)} pagos
                    {isPartial && (
                        <span className="text-warning font-medium"> · parcial</span>
                    )}
                </>
            }
            actions={
                <>
                    {item.externalRefund ? (
                        <span className="flex items-center gap-1">
                            {/* ⚠️ NÃO diz "Estornado": a marcação não estorna nada — ela
                                só registra que o dinheiro voltou por fora. Enquanto
                                ninguém aprovar, a cobrança segue PAID e o
                                `refundedAmount` vazio. O texto anterior ("Estornado via
                                X", em azul de coisa concluída) fazia o operador achar
                                que havia terminado: em produção quatro pedidos ficaram
                                parados assim, com o dinheiro já devolvido ao cliente e a
                                cobrança dizendo que estava paga. */}
                            <span
                                className="badge badge-warning gap-1"
                                title={`Marcado por ${item.externalRefund.byName || 'usuário'}. A cobrança só passa a estornada quando alguém clicar em Aprovar — a marcação apenas evita estornar de novo pela conta digital.`}
                            >
                                <MdAccountBalance className="text-sm" />
                                Devolvido via {item.externalRefund.via} · falta
                                aprovar
                            </span>
                            <button
                                className="btn btn-xs btn-ghost"
                                title="Desfazer marcação"
                                onClick={onClearExternal}
                                disabled={marking}
                            >
                                <MdClose />
                            </button>
                        </span>
                    ) : (
                        <button
                            className="btn btn-sm btn-ghost gap-1 text-info"
                            onClick={onMarkExternal}
                            disabled={marking}
                            title="Registra que o dinheiro voltou por fora (outro banco, espécie). A cobrança só é estornada quando alguém aprovar."
                        >
                            <MdAccountBalance />
                            Já foi estornado
                        </button>
                    )}

                    {/* Antes de recusar/aprovar: é a saída de quem errou o pedido, não
                        uma decisão sobre ele. Independe de ser operador — quem tem
                        `delete` na fila pode desfazer. */}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-ghost text-error"
                            onClick={onDelete}
                            title="Excluir da fila sem decidir: a cobrança volta ao estado normal"
                        >
                            <MdDelete /> Excluir
                        </button>
                    )}

                    {canApprove ? (
                        <>
                            <button
                                className="btn btn-sm btn-error btn-outline"
                                onClick={onReject}
                            >
                                <MdClose /> Recusar
                            </button>
                            <button
                                className="btn btn-sm btn-success"
                                onClick={onApprove}
                            >
                                <MdCheck /> Aprovar
                            </button>
                        </>
                    ) : (
                        /* A razão de não poder aprovar muda com o método: em dinheiro,
                           maquininha e "outro" o que falta é permissão de exclusão na
                           role (nada sai da conta digital); nos demais, é ser operador
                           financeiro eleito. Dizer o motivo certo evita o operador
                           procurar a permissão errada. */
                        <span className="text-xs text-base-content/50">
                            Requer permissão de exclusão em Financeiro &gt;
                            Aprovações
                            {isOffline
                                ? ''
                                : ' (ou ser operador financeiro, para estornar pela conta digital)'}
                        </span>
                    )}
                </>
            }
        >
            <div className="flex flex-wrap items-center gap-2">
                {!item.billing.canRefundViaBaas && (
                    <span className="badge badge-ghost badge-sm">
                        Pago fora da conta digital
                    </span>
                )}
                {/* Parcelamento no provedor: o estorno atinge o parcelamento, não a
                    parcela escolhida — o operador precisa saber antes de aprovar. */}
                {item.billing.isProviderInstallment && (
                    <span className="badge badge-warning badge-sm">
                        Cartão parcelado — estorno no parcelamento
                    </span>
                )}
                {item.payload?.refundReason && (
                    <span className="text-sm text-base-content/70 rounded-lg bg-base-200/50 px-3 py-1.5 flex-1 min-w-0">
                        {item.payload.refundReason}
                    </span>
                )}
            </div>
        </ApprovalCard>
    )
}
